import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseService } from '../../services/course/course.service';
import { UnifiedCartService } from '../../services/unified-cart/unified-cart.service';
import { CourseModel, CourseUnitModel } from '../../core/models';
import { availableClusterOffers, clusterSaving } from '../../core/cluster-pricing';
import { courseResourceUrl } from '../../core/course-content';

@Component({
  selector: 'app-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrls: ['./course-detail.component.scss']
})
export class CourseDetailComponent implements OnInit, OnDestroy {
  course: CourseModel | null = null;
  loading = false;
  message = '';
  downloadingBrochure = false;
  // Unit groups are collapsed by default to match the supplied course design.
  coreUnitsOpen = false;
  electiveUnitsOpen = false;
  private routeSubscription?: Subscription;
  private courseSubscription?: Subscription;
  resourceUrl = courseResourceUrl;
  offerSaving = clusterSaving;

  constructor(private route: ActivatedRoute, private router: Router, private courseService: CourseService, private cartService: UnifiedCartService) {}

  ngOnInit(): void { this.routeSubscription = this.route.paramMap.subscribe(() => this.loadPageData()); }

  loadPageData(): void {
    this.courseSubscription?.unsubscribe();
    this.course = null;
    this.message = '';
    this.loading = true;
    const courseId = Number(this.route.snapshot.paramMap.get('id'));
    this.courseSubscription = this.courseService.getCourseById(courseId).subscribe({
      next: course => {
        this.loading = false;
        if (!course || course.courseId !== courseId || !course.isActive) {
          this.message = 'This course is currently unavailable.';
          return;
        }
        this.course = course;
      },
      error: () => { this.loading = false; this.message = 'This course could not be loaded. Please try again.'; }
    });
  }


  get backToCoursesQueryParams(): any {
    const qp = this.route.snapshot.queryParamMap;
    return {
      view: qp.get('returnView') === 'list' ? 'list' : null,
      tagSlug: qp.get('tagSlug') || null,
      qualificationType: qp.get('qualificationType') || null,
      keyword: qp.get('keyword') || null,
      courseId: qp.get('courseId') || null,
      page: qp.get('page') || null,
      pageSize: qp.get('pageSize') || null
    };
  }

  get brochureLink(): string | null { return courseResourceUrl(this.course?.brochureUrl); }
  /**
   * HTML rendered in the About This Course section.
   *
   * Quill/RTE content can contain non-breaking spaces (&nbsp; / NBSP).
   * Those prevent normal line wrapping and can push the About panel wider
   * than the viewport. Convert only those spaces while preserving all
   * other RTE markup and formatting.
   */
  get aboutCourseHtml(): string {
    const html =
      this.course?.fullDescription ||
      this.course?.shortDescription ||
      '';

    return this.normalizeRteSpacing(html);
  }

  get currency(): string { return this.course?.currencyCode || 'AUD'; }
  get offers(): CourseModel[] { return availableClusterOffers(this.course?.courseId || 0, this.course?.availableClusters || []); }
  /**
   * Public title shown throughout the page. The qualification and course name
   * stay independent in the data model, while the public heading always ends
   * with the course category/specialisation in brackets.
   */
  get displayCourseTitle(): string {
    const main = this.displayCourseTitleMain;
    const accent = this.displayCourseTitleAccent;
    return [main, accent].filter(Boolean).join(' ').trim();
  }

  /**
   * Main qualification title without the trailing category/specialisation.
   * Example: "Certificate III in Individual Support".
   */
   get displayCourseTitleMain(): string {
  const rawName = (this.course?.courseName || '').trim();
  const qualification = (this.course?.qualificationType || '').trim();

  const nameWithoutCategory = rawName
    .replace(/\s*\([^()]+\)\s*$/, '')
    .trim();

  // Skip qualification and connector when type is None or empty
  if (!qualification || qualification.toLowerCase() === 'none') {
    return nameWithoutCategory;
  }

  if (!nameWithoutCategory) {
    return qualification;
  }

  // Avoid duplicate qualification in course name
  if (nameWithoutCategory.toLowerCase().startsWith(qualification.toLowerCase())) {
    return nameWithoutCategory;
  }

  const connector = /^(diploma|advanced diploma)$/i.test(qualification)
    ? ' of '
    : ' in ';

  return `${qualification}${connector}${nameWithoutCategory}`;
}

  /**
   * Category/specialisation is always rendered on its own line in brackets.
   * Priority:
   *  1. Existing trailing value from CourseName, e.g. "(Ageing and Disability)".
   *  2. First meaningful course category/tag from the API.
   */
  get displayCourseTitleAccent(): string {
    const rawName = (this.course?.courseName || '').trim();
    const inlineCategory = rawName.match(/\(([^()]+)\)\s*$/)?.[1]?.trim();
    const category = inlineCategory || this.displayCourseCategory;
    return category ? `(${category})` : '';
  }

  /** Returns the first real course category, excluding generic catalogue tags. */
  get displayCourseCategory(): string {
    const ignored = new Set([
      'long course', 'long courses', 'short course', 'short courses',
      'course', 'courses', 'qualification', 'qualifications'
    ]);

    const tag = (this.course?.tags || []).find(item => {
      const name = (item?.tagName || '').trim();
      return !!name && !ignored.has(name.toLowerCase());
    });

    return (tag?.tagName || '').trim();
  }

  /** Delivery values are stored as an array for the new long-course presentation, with legacy fallback. */
  get deliveryModes(): string[] {
    const configured = (this.course?.deliveryOptions || []).map(x => (x || '').trim()).filter(Boolean);
    if (configured.length) return configured;
    const legacy = (this.course?.deliveryMode || '').split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
    return legacy;
  }

  /** Prerequisites are dynamic; when empty the About panel automatically becomes full width. */
  get prerequisites(): string[] {
    return (this.course?.coursePrerequisites || []).map(x => (x || '').trim()).filter(Boolean);
  }

  get outcomes(): string[] {
    return (this.course?.employmentOutcomes || []).filter(outcome => !!outcome?.trim());
  }

  /** Only valid relational pathways are rendered. */
  get pathways() {
    return (this.course?.studyPathways || []).filter(
      pathway => Number(pathway?.relatedCourseId) > 0 && !!pathway?.courseName?.trim()
    );
  }

  get coreUnits(): CourseUnitModel[] { return this.units('core'); }
  get electiveUnits(): CourseUnitModel[] { return this.units('elective'); }
  get enquiryParams() { return { course: this.course?.courseName, courseCode: this.course?.courseCode }; }

  /**
   * Returns true only when the course has an image configured in the API/Core model.
   * Empty/whitespace values deliberately use the supplied ECB aged-care hero artwork.
   */
  get hasConfiguredCourseImage(): boolean {
    return !!(this.course?.imageUrl || '').trim();
  }

  /**
   * Public hero image source. The local asset is the default for long courses that
   * do not yet have an ImageUrl configured in the admin/API data.
   */
  get heroImageUrl(): string {
    return this.hasConfiguredCourseImage
      ? (this.course?.imageUrl || '').trim()
      : 'assets/images/default-long-course-hero.png';
  }

  private units(type: 'core' | 'elective'): CourseUnitModel[] {
    return (this.course?.courseUnits || []).filter(unit => unit.isActive !== false && !!unit.name?.trim() &&
      (unit.unitType || 'core') === type).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }

  scrollTo(id: string): void {
    const section = document.getElementById(id);
    section?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    section?.focus({ preventScroll: true });
  }

  /**
   * Adds the current course to the cart and immediately opens the cart page.
   * If the course is already in the cart we simply open the cart, avoiding a
   * duplicate-cart error and keeping the CTA predictable for the customer.
   */
  /**
   * Downloads the brochure through the ECB API.
   *
   * The API fetches the external PDF server-side and returns it with
   * Content-Disposition: attachment. This avoids browser redirection and
   * cross-origin download limitations.
   */
  downloadBrochure(): void {
    if (!this.course?.courseId || !this.course?.brochureUrl?.trim() || this.downloadingBrochure) {
      return;
    }

    this.downloadingBrochure = true;

    this.courseService.downloadCourseBrochure(this.course.courseId).subscribe({
      next: response => {
        this.downloadingBrochure = false;

        const blob = response.body;
        if (!blob) {
          return;
        }

        const contentDisposition = response.headers.get('content-disposition') || '';
        const fileNameMatch = /filename\\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition);
        const fallbackName = `${this.course?.courseCode || 'course'}-brochure.pdf`;
        const fileName = fileNameMatch
          ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
          : fallbackName;

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = fileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(objectUrl);
      },
      error: error => {
        this.downloadingBrochure = false;
        console.error('Brochure download failed', error);
        this.message = 'Unable to download the brochure. Please try again.';
      }
    });
  }

  showingOccurrencePicker = false;
  addToCartAndViewCart(): void { if (this.course) this.showingOccurrencePicker = true; }
  bookNow(): void { this.addToCartAndViewCart(); }
  isInCart(): boolean { return !!this.course && this.cartService.isSelected('QUALIFICATION', this.course.courseId); }
  isOfferSelected(id: number): boolean { return this.cartService.isSelected('QUALIFICATION', id); }

  /**
   * If a configured course image cannot be loaded, fall back to the same local
   * hero artwork used when ImageUrl is empty rather than showing a generic placeholder.
   */
  imageFallback(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    image.src = 'assets/images/default-long-course-hero.png';
  }

  private normalizeRteSpacing(
    value: string | null | undefined
  ): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#160;/gi, ' ')
      .replace(/&#xA0;/gi, ' ')
      .replace(/\u00A0/g, ' ');
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.courseSubscription?.unsubscribe();
  }
}
